import React, { useState, useRef, useEffect } from 'react';
import { useShowDebugPopups } from '@/routes/TestUi/components/hooks/use-show-debug-popups';
import './styles/debug-menu.scss';

import {
  setGameStatus,
  setGeolocationStatus,
  setGameWindowContext,
  setRecentlyPlayedContext,
} from '@/store/actions';
import { useStore } from '@/store/useStore';
import {
  getSession,
  getSessionWithLoginView,
  isCurrentLocalSessionValid,
} from '@/unified-modules/account/account-library';
import { useGeolocationHelpers } from '@/geolocation/geolocation-web/use-geolocation-helper';
import { getRegion } from '@/utilities/getters/cookieGetters';
import { useShallow } from 'zustand/react/shallow';
import { Account } from '@fanduel/cpe-um-bundle/account/library';
import {
  GAME_LAUNCH_SOURCE,
  setPendingGameLaunchSource,
} from '@/utilities/logger/snowplow/game-launch-source';
import { usePopup } from '@/features/Popup/usePopup';
import { launchNextGame } from '@/utilities/helpers/launchNextGame';
import { useNavigate } from 'react-router-dom';
import {
  TRACK_USER_ACTION_EVENT,
  trackUserAction,
} from '@/utilities/logger/amplitude/tracking';
import useSoftReload from '@/lostHooks/softReload/useSoftReload';
import useGameUidParam from '@/lostHooks/gameUid/useGameUid';
import { useGLS } from '@/game-client/hooks/use-gls';
import { runJackpotGlsReconcile } from '@/game-client/jackpot-gls-reconcile-run';
import { recordGlsResponseJackpotMode } from '@/game-client/gls-jackpot-mode-tracker';
import { setJackpotModeUrlParam } from '@/features/SWJ/helpers/jackpot-mode-launch';
import { resolveLaunchVitalsReconciliation } from '@/utilities/logger/datadog/vitals';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import { isEmbeddedInLobby } from '@/logic/embedded-mode';
import { getLobbyWebWrapper } from '@/logic/web-wrapper/web-wrapper-bridge';
type DebugMenuProps = {
  tryAgain: () => void;
  tryAgainWithLogout: () => void;
};

const DebugMenu: React.FC<DebugMenuProps> = ({
  tryAgain,
  tryAgainWithLogout,
}) => {
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const debugPopups = useShowDebugPopups();
  const { reload } = useSoftReload();
  const { fetchGame } = useGLS();
  const urlGameUid = useGameUidParam();
  const { hidePopup } = usePopup();
  const { locateUser } = useGeolocationHelpers();
  const forceSwjBar = useStore((s) => s.gameStatus.forceSwjBar);

  const gameSwitchInProgress = useStore(
    (s) => s.gameWindowContext.gameSwitchInProgress,
  );
  const gameContext = useStore((s) => s.gameContext);
  const commonUIContext = useStore((s) => s.commonUIContext);

  const geolocationFailed = useStore(
    (s) => s.geolocationStatus.geolocationFailed,
  );

  const { userId, authToken, sessionId } = useStore(
    useShallow((state) => ({
      userId: state.userSessionContext.userId,
      authToken: state.userSessionContext.authToken,
      sessionId: state.userSessionContext.sessionId,
      hasActiveSession: state.userSessionContext.hasActiveSession,
    })),
  );

  const fireInGameExit = () => {
    const gameWindowOpen =
      useStore.getState().gameWindowContext?.gameWindowOpen;
    trackUserAction({
      event: TRACK_USER_ACTION_EVENT.IN_GAME_EXITED,
      module: gameWindowOpen ? 'IN_GAME' : 'IN_GAME_HEADER',
      zone: gameWindowOpen ? 'DASHBOARD' : 'IN_GAME',
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMenuAction = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  const handleMenuActionAsync = (action: () => Promise<void>) => {
    void action().finally(() => setIsOpen(false));
  };

  const triggerSilentJackpotGlsReconcile = async () => {
    hidePopup();
    recordGlsResponseJackpotMode(true);
    resolveLaunchVitalsReconciliation(true);
    setJackpotModeUrlParam(false);

    Logger.logRumAction({
      name: '[RECON] GLS_JACKPOT_RECONCILIATION',
      useCase: DdEventCategory.GameLaunch,
      eventType: DdEventType.Info,
      context: { source: 'debug_menu', forced: true },
    });

    await runJackpotGlsReconcile(fetchGame, urlGameUid, {
      launchVitals: 'preserve',
    });
  };

  return (
    <>
      <button
        ref={buttonRef}
        className="debug-menu__button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Debug Menu"
      >
        Debug
      </button>
      {isOpen && (
        <div ref={menuRef} className="debug-menu__dropdown">
          <div className="debug-menu__header">Debug Menu</div>
          <div className="debug-menu__actions">
            <button
              className="debug-menu__action"
              onClick={() => handleMenuAction(tryAgain)}
            >
              Try again
            </button>
            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuActionAsync(triggerSilentJackpotGlsReconcile)
              }
            >
              Silent jackpot GLS reconcile (auto flow)
            </button>

            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuAction(() => {
                  setRecentlyPlayedContext({
                    nextGameUid: 'cash-eruption',
                    nextGameUrl: `${window.location.origin}/cash-eruption`,
                  });
                  hidePopup();
                  fireInGameExit();
                  setPendingGameLaunchSource(
                    GAME_LAUNCH_SOURCE.RECENTLY_PLAYED_IN_GAME,
                  );
                  void launchNextGame(reload, navigate);
                })
              }
            >
              Try again: Launch new game
            </button>

            <button
              className="debug-menu__action"
              onClick={() => handleMenuAction(tryAgainWithLogout)}
            >
              Try again with logout
            </button>
            <button
              className="debug-menu__action"
              onClick={() => handleMenuAction(debugPopups.showExitPopup)}
            >
              Exit Popup
            </button>
            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuAction(debugPopups.showExitRecentlyPopup)
              }
            >
              Exit Recently Played Popup
            </button>
            <button
              className="debug-menu__action"
              onClick={() => handleMenuAction(debugPopups.showSWJPopup)}
            >
              SWJ Popup
            </button>
            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuAction(debugPopups.showRealityCheckPopup)
              }
            >
              Reality Check Popup
            </button>
            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuAction(debugPopups.showFreeSpinDialogChoosePopup)
              }
            >
              Free Spin Dialog Choose Popup
            </button>
            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuAction(debugPopups.showFreeSpinDialogStartPopup)
              }
            >
              Free Spin Dialog Start Popup
            </button>
            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuAction(debugPopups.showFreeSpinDialogFinishPopup)
              }
            >
              Free Spin Dialog Finish Popup
            </button>
            <button
              className="debug-menu__action"
              onClick={() => {
                if (isEmbeddedInLobby()) {
                  const wrapper = getLobbyWebWrapper();
                  wrapper?.triggerLocateUser();
                }
                locateUser({
                  region: getRegion(),
                  sessionId: sessionId ?? 'session_id',
                  userId: userId ?? 'user_id',
                  userAuthToken: (authToken ?? 'auth_token') as string,
                  product: 'casino',
                });
              }}
            >
              Trigger Locate User
            </button>

            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuAction(debugPopups.showGeolocationRefreshLoadingPopup)
              }
            >
              Geolocation Refresh (Loading)
            </button>
            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuAction(debugPopups.showGeolocationRefreshSuccessPopup)
              }
            >
              Geolocation Refresh (Success)
            </button>
            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuAction(debugPopups.showGeolocationRefreshErrorPopup)
              }
            >
              Geolocation Refresh (Error)
            </button>
            <button
              className="debug-menu__action"
              onClick={() => handleMenuAction(debugPopups.popupQueue)}
            >
              Test Popup Queueing
            </button>

            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuAction(() =>
                  setGameStatus({ forceSwjBar: !forceSwjBar }),
                )
              }
            >
              Show SWJ TopBar
            </button>

            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuAction(() =>
                  setGameWindowContext({
                    gameSwitchInProgress: !gameSwitchInProgress,
                  }),
                )
              }
            >
              Turn Game Switch Overlay {gameSwitchInProgress ? 'OFF' : 'ON'}
            </button>

            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuAction(() =>
                  console.log('[DEBUG][GAME_SWITCH] current game context', {
                    gameContext,
                    commonUIContext,
                    gameSwitchInProgress,
                  }),
                )
              }
            >
              Log game context
            </button>

            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuAction(() => setGameStatus({ gameFetchFailed: true }))
              }
            >
              Set fgp fail
            </button>

            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuAction(() =>
                  setGeolocationStatus({
                    geolocationFailed: !geolocationFailed,
                  }),
                )
              }
            >
              Set location failed
            </button>

            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuActionAsync(async () => {
                  console.log('[DEBUG] calling getLoginToken...');
                  const loginToken = await getSession();
                  console.log('[DEBUG] getLoginToken result:', loginToken);
                })
              }
            >
              getSessionWithLoginView
            </button>
            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuActionAsync(async () => {
                  console.log('[DEBUG] calling getLoginToken...');
                  const session = await Account.checkValidSession();
                  console.log('[DEBUG] session result:', session);
                })
              }
            >
              checkValidSession()
            </button>

            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuActionAsync(async () => {
                  console.log('[DEBUG] calling getLoginToken...');
                  const session = await Account.getSessionAsync();
                  console.log('[DEBUG] Session result:', session);
                })
              }
            >
              getSessionAsync()
            </button>
            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuActionAsync(async () => {
                  console.log('[DEBUG] calling getSession...');
                  const session = await getSessionWithLoginView();
                  console.log('[DEBUG] getSession result:', session);
                })
              }
            >
              getSession
            </button>
            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuActionAsync(async () => {
                  console.log('[DEBUG] calling isCurrentLocalSessionValid...');
                  const validSession = await isCurrentLocalSessionValid();
                  console.log(
                    '[DEBUG] isCurrentLocalSessionValid result:',
                    validSession,
                  );
                })
              }
            >
              isCurrentLocalSessionValid
            </button>
            <button
              className="debug-menu__action"
              onClick={() =>
                handleMenuAction(() => {
                  const { amplitudeExperiments } = useStore.getState();
                  console.log('[DEBUG][EXPERIMENTS]', {
                    amplitudeExperiments,
                  });
                })
              }
            >
              Log experiments
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default DebugMenu;
